import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Building2, DollarSign, Shield, CreditCard, ArrowRight, ArrowLeft,
  CheckCircle, Wallet, FileText, AlertTriangle, Package, Truck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { num: 1, label: 'Business Profile', icon: Building2 },
  { num: 2, label: 'Pricing Markup', icon: DollarSign },
  { num: 3, label: 'Insurance & Protection', icon: Shield },
  { num: 4, label: 'Billing Terms', icon: CreditCard }
];

const OnboardingPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Step 1
  const [s1, setS1] = useState({ company_name: '', business_type: 'b2c', monthly_volume: '1-50', primary_marketplace: 'custom' });
  // Step 2
  const [s2, setS2] = useState({ default_markup_percent: 20, base_add: 1.50, per_pound_add: 0.75, surcharge_mode: 'percent', surcharge_markup: 10 });
  // Step 3
  const [s3, setS3] = useState({ shipping_protection_tier: 'standard', payment_protection_tier: 'standard', alternative_protection_tier: 'basic', return_protection_tier: 'basic' });
  // Step 4
  const [s4, setS4] = useState({ billing_method: 'credit_card', acknowledge_billing_terms: false });

  const [protectionSummary, setProtectionSummary] = useState(null);

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/config`);
      if (res.ok) setConfig(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const saveStep = async (stepNum, data) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/step/${stepNum}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        const result = await res.json();
        if (result.protection_summary) setProtectionSummary(result.protection_summary);
        if (stepNum < 4) setStep(stepNum + 1);
        else navigate('/dashboard');
      } else {
        const err = await res.json();
        alert(err.detail || 'Error saving');
      }
    } catch (err) { console.error(err); alert('Save failed'); }
    finally { setSaving(false); }
  };

  if (loading || !config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="onboarding-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-lg">Account Setup</h1>
              <p className="text-xs text-slate-400">Step {step} of 4</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {STEPS.map((s) => (
              <div key={s.num} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                s.num === step ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' :
                s.num < step ? 'bg-emerald-500 text-white' :
                'bg-slate-100 text-slate-400 dark:bg-slate-800'
              }`}>
                {s.num < step ? <CheckCircle className="w-3 h-3" /> : <s.icon className="w-3 h-3" />}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-10">

        {/* STEP 1: Business Profile */}
        {step === 1 && (
          <div className="space-y-6" data-testid="step-1">
            <div>
              <h2 className="text-2xl font-semibold mb-1">Tell us about your business</h2>
              <p className="text-slate-500">This helps us configure the right features and limits for you.</p>
            </div>
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 space-y-5">
              <div>
                <label className="text-sm font-medium block mb-1.5">Company Name</label>
                <input value={s1.company_name} onChange={e => setS1(p => ({...p, company_name: e.target.value}))} placeholder="Your company name" className="w-full h-11 px-4 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800" data-testid="ob-company" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Business Type</label>
                <div className="grid grid-cols-2 gap-3">
                  {config.business_types.map(bt => (
                    <button key={bt.id} type="button" onClick={() => setS1(p => ({...p, business_type: bt.id}))}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${s1.business_type === bt.id ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'}`}
                      data-testid={`bt-${bt.id}`}
                    >
                      <p className="font-semibold text-sm">{bt.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{bt.description}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Monthly Shipping Volume</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {config.volume_tiers.map(vt => (
                    <button key={vt.id} type="button" onClick={() => setS1(p => ({...p, monthly_volume: vt.id}))}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${s1.monthly_volume === vt.id ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-200 dark:border-slate-700'}`}
                      data-testid={`vol-${vt.id}`}
                    >
                      <p className="font-semibold text-sm">{vt.name}</p>
                      <p className="text-[10px] text-slate-400">{vt.label}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Primary Marketplace</label>
                <div className="flex flex-wrap gap-2">
                  {config.marketplaces.map(m => (
                    <button key={m.id} type="button" onClick={() => setS1(p => ({...p, primary_marketplace: m.id}))}
                      className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${s1.primary_marketplace === m.id ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700' : 'border-slate-200 dark:border-slate-700'}`}
                      data-testid={`mp-${m.id}`}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <Button onClick={() => saveStep(1, s1)} disabled={saving || !s1.company_name} className="w-full h-12" data-testid="step1-next">
              {saving ? 'Saving...' : 'Continue'} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* STEP 2: Pricing Markup */}
        {step === 2 && (
          <div className="space-y-6" data-testid="step-2">
            <div>
              <h2 className="text-2xl font-semibold mb-1">Set your pricing markup</h2>
              <p className="text-slate-500">This determines how much you earn on each shipment. Applied across all 5 gateways.</p>
            </div>
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 space-y-5">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                Limits: Markup {config.markup_limits.min_percent}% – {config.markup_limits.max_percent}% | Base add ${config.markup_limits.min_base_add} – ${config.markup_limits.max_base_add} | Per-pound $0 – ${config.markup_limits.max_per_pound}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1.5">Default Markup %</label>
                  <input type="number" min={config.markup_limits.min_percent} max={config.markup_limits.max_percent} value={s2.default_markup_percent} onChange={e => setS2(p => ({...p, default_markup_percent: parseFloat(e.target.value) || 0}))} className="w-full h-11 px-4 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 tabular-nums" data-testid="ob-markup" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1.5">Base Add ($)</label>
                  <input type="number" step="0.25" min={0} max={config.markup_limits.max_base_add} value={s2.base_add} onChange={e => setS2(p => ({...p, base_add: parseFloat(e.target.value) || 0}))} className="w-full h-11 px-4 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 tabular-nums" data-testid="ob-base-add" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1.5">Per-Pound Add ($)</label>
                  <input type="number" step="0.25" min={0} max={config.markup_limits.max_per_pound} value={s2.per_pound_add} onChange={e => setS2(p => ({...p, per_pound_add: parseFloat(e.target.value) || 0}))} className="w-full h-11 px-4 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 tabular-nums" data-testid="ob-per-pound" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1.5">Surcharge Markup</label>
                  <div className="flex gap-2">
                    <select value={s2.surcharge_mode} onChange={e => setS2(p => ({...p, surcharge_mode: e.target.value}))} className="h-11 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm">
                      <option value="percent">Percent</option>
                      <option value="flat">Flat ($)</option>
                    </select>
                    <input type="number" step="0.5" value={s2.surcharge_markup} onChange={e => setS2(p => ({...p, surcharge_markup: parseFloat(e.target.value) || 0}))} className="flex-1 h-11 px-4 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 tabular-nums" data-testid="ob-surcharge" />
                  </div>
                </div>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-xs text-slate-400 mb-2">EXAMPLE: $10 carrier rate</p>
                <div className="flex items-center gap-6 text-sm">
                  <span>Carrier: <strong className="tabular-nums">$10.00</strong></span>
                  <span>+ Markup ({s2.default_markup_percent}%): <strong className="tabular-nums">${(10 * s2.default_markup_percent / 100).toFixed(2)}</strong></span>
                  <span>+ Base: <strong className="tabular-nums">${s2.base_add.toFixed(2)}</strong></span>
                  <span>= Sell: <strong className="text-emerald-600 tabular-nums">${(10 + 10 * s2.default_markup_percent / 100 + s2.base_add).toFixed(2)}</strong></span>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="h-12"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
              <Button onClick={() => saveStep(2, s2)} disabled={saving} className="flex-1 h-12" data-testid="step2-next">
                {saving ? 'Saving...' : 'Continue'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3: Insurance & Protection */}
        {step === 3 && (
          <div className="space-y-6" data-testid="step-3">
            <div>
              <h2 className="text-2xl font-semibold mb-1">Insurance & Protection Limits</h2>
              <p className="text-slate-500">Choose protection tiers for each category. Higher tiers = higher coverage limits.</p>
            </div>
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 space-y-6">
              {[
                { key: 'shipping_protection_tier', label: 'Shipping Protection', desc: 'Loss, theft, damage in transit', icon: Truck, limits: config.protection_limits.shipping, rates: config.insurance_rates.shipping },
                { key: 'payment_protection_tier', label: 'Payment Protection', desc: 'Fraud, chargebacks, disputes', icon: CreditCard, limits: config.protection_limits.payment, rates: config.insurance_rates.payment },
                { key: 'alternative_protection_tier', label: 'Alternative Protection', desc: 'Wrong items, quality issues', icon: Package, limits: config.protection_limits.alternative, rates: config.insurance_rates.alternative },
                { key: 'return_protection_tier', label: 'Return Protection', desc: 'Return shipping, restocking', icon: ArrowLeft, limits: config.protection_limits.return, rates: config.insurance_rates.return }
              ].map(({ key, label, desc, icon: Icon, limits, rates }) => (
                <div key={key}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4 text-emerald-600" />
                    <span className="font-semibold text-sm">{label}</span>
                    <span className="text-xs text-slate-400">— {desc}</span>
                  </div>
                  <p className="text-xs text-slate-400 mb-2">Coverage: ${limits.min.toLocaleString()} – ${limits.max.toLocaleString()} | Rate: {(rates.min_rate * 100).toFixed(1)}% – {(rates.max_rate * 100).toFixed(1)}%</p>
                  <div className="flex flex-wrap gap-2">
                    {limits.tiers.map(tier => (
                      <button key={tier} type="button" onClick={() => setS3(p => ({...p, [key]: tier}))}
                        className={`px-4 py-2 rounded-lg border-2 text-sm font-medium capitalize transition-all ${s3[key] === tier ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700' : 'border-slate-200 dark:border-slate-700'}`}
                        data-testid={`${key}-${tier}`}
                      >
                        {tier}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {protectionSummary && (
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800 p-4">
                <p className="text-sm font-semibold text-emerald-700 mb-2">Protection Summary</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {Object.entries(protectionSummary).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="capitalize text-slate-600">{k}</span>
                      <span className="font-medium">${v.max_coverage?.toLocaleString()} @ {v.rate_display}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)} className="h-12"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
              <Button onClick={() => saveStep(3, s3)} disabled={saving} className="flex-1 h-12" data-testid="step3-next">
                {saving ? 'Saving...' : 'Continue'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 4: Billing Terms */}
        {step === 4 && (
          <div className="space-y-6" data-testid="step-4">
            <div>
              <h2 className="text-2xl font-semibold mb-1">Billing Terms</h2>
              <p className="text-slate-500">Review and acknowledge our billing policy to complete setup.</p>
            </div>
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 space-y-5">
              <div className="p-5 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-4">
                <p className="text-slate-700 dark:text-slate-200 leading-relaxed">
                  Our programs are designed to give you optimal protection and convenience. Our billing policies are simple.
                </p>
                <p className="text-slate-700 dark:text-slate-200 leading-relaxed">
                  We expect to be paid upon demand. This is either through our wallet, paid by credit card, or by other certified funds. We give our all for you and provide the protections when you need them.
                </p>
                <p className="font-semibold text-slate-900 dark:text-white leading-relaxed">
                  We will not allow terms or unpaid bills. It's simple — if your bills are not paid then your service or protection is not valid.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium block mb-2">Payment Method</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'wallet', label: 'SelleSend Wallet', desc: 'Pre-funded balance', icon: Wallet },
                    { id: 'credit_card', label: 'Credit Card', desc: 'Instant payment', icon: CreditCard },
                    { id: 'certified_funds', label: 'Certified Funds', desc: 'Wire / ACH', icon: FileText }
                  ].map(m => (
                    <button key={m.id} type="button" onClick={() => setS4(p => ({...p, billing_method: m.id}))}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${s4.billing_method === m.id ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-200 dark:border-slate-700'}`}
                      data-testid={`bill-${m.id}`}
                    >
                      <m.icon className="w-5 h-5 text-slate-500 mb-2" />
                      <p className="font-semibold text-sm">{m.label}</p>
                      <p className="text-[10px] text-slate-400">{m.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                <input type="checkbox" checked={s4.acknowledge_billing_terms} onChange={e => setS4(p => ({...p, acknowledge_billing_terms: e.target.checked}))} className="mt-1 w-5 h-5 rounded" data-testid="acknowledge-terms" />
                <div>
                  <p className="font-semibold text-sm">I acknowledge and agree to the billing terms</p>
                  <p className="text-xs text-slate-500 mt-0.5">Payment is due on demand. Services are valid only while bills are current.</p>
                </div>
              </label>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(3)} className="h-12"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
              <Button onClick={() => saveStep(4, s4)} disabled={saving || !s4.acknowledge_billing_terms} className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700" data-testid="step4-complete">
                {saving ? 'Completing...' : 'Complete Setup'} <CheckCircle className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
